import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart
} from 'recharts';
import { 
  TrendingUp, TrendingDown, Building2, Users, DollarSign, Activity,
  BarChart3, PieChart as PieChartIcon, Download, Sparkles, RefreshCw,
  Calendar, Target, AlertTriangle, ChevronRight, Trophy, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#0D9488', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444', '#10B981', '#6366F1'];

const AdvancedReports = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState('last_30_days');
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [rankings, setRankings] = useState(null);
  const [revenueBreakdown, setRevenueBreakdown] = useState(null);
  const [growthMetrics, setGrowthMetrics] = useState(null);
  const [churnAnalysis, setChurnAnalysis] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [rankingMetric, setRankingMetric] = useState('revenue');
  const [breakdownBy, setBreakdownBy] = useState('clinic');
  const [showAiDialog, setShowAiDialog] = useState(false);

  useEffect(() => {
    fetchOverview();
  }, []);

  useEffect(() => {
    if (activeTab === 'rankings') {
      fetchRankings();
    } else if (activeTab === 'revenue') {
      fetchRevenueBreakdown();
    } else if (activeTab === 'growth') {
      fetchGrowthMetrics();
    } else if (activeTab === 'churn') {
      fetchChurnAnalysis();
    }
  }, [activeTab, period, rankingMetric, breakdownBy]);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/platform-reports/overview`);
      setOverview(response.data);
    } catch (error) {
      console.error('Failed to fetch overview:', error);
      toast.error('Failed to load platform overview');
    } finally {
      setLoading(false);
    }
  };

  const fetchRankings = async () => {
    try {
      const response = await axios.get(`${API}/platform-reports/clinic-rankings`, {
        params: { metric: rankingMetric, period, limit: 10 }
      });
      setRankings(response.data);
    } catch (error) {
      console.error('Failed to fetch rankings:', error);
    }
  };

  const fetchRevenueBreakdown = async () => {
    try {
      const response = await axios.get(`${API}/platform-reports/revenue-breakdown`, {
        params: { period, breakdown_by: breakdownBy }
      });
      setRevenueBreakdown(response.data);
    } catch (error) {
      console.error('Failed to fetch revenue breakdown:', error);
    }
  };

  const fetchGrowthMetrics = async () => {
    try {
      const response = await axios.get(`${API}/platform-reports/growth-metrics`, {
        params: { period: 'last_12_months' }
      });
      setGrowthMetrics(response.data);
    } catch (error) {
      console.error('Failed to fetch growth metrics:', error);
    }
  };

  const fetchChurnAnalysis = async () => {
    try {
      const response = await axios.get(`${API}/platform-reports/churn-analysis`, {
        params: { period }
      });
      setChurnAnalysis(response.data);
    } catch (error) {
      console.error('Failed to fetch churn analysis:', error);
    }
  };

  const fetchAiInsights = async () => {
    try {
      setAiLoading(true);
      const response = await axios.post(`${API}/platform-reports/ai-platform-insights`, {
        focus_area: 'overall'
      });
      setAiInsights(response.data);
      setShowAiDialog(true);
    } catch (error) {
      console.error('Failed to fetch AI insights:', error);
      toast.error('Failed to generate AI insights');
    } finally {
      setAiLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const getPeriodLabel = (p) => {
    const labels = {
      'last_7_days': 'Last 7 Days',
      'last_30_days': 'Last 30 Days',
      'last_90_days': 'Last 90 Days',
      'last_12_months': 'Last 12 Months'
    };
    return labels[p] || p;
  };

  const exportReport = async (type) => {
    try {
      const response = await axios.post(`${API}/platform-reports/export`, null, {
        params: { report_type: type, format: 'csv', period }
      });
      
      // Create download
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `platform_${type}_report.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Report exported successfully');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Platform Reports & Analytics</h1>
          <p className="text-gray-600 mt-1">Comprehensive insights across all clinics</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 bg-white focus:ring-2 focus:ring-teal-500"
          >
            <option value="last_7_days">Last 7 Days</option>
            <option value="last_30_days">Last 30 Days</option>
            <option value="last_90_days">Last 90 Days</option>
          </select>
          <Button 
            onClick={fetchAiInsights} 
            disabled={aiLoading}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
          >
            {aiLoading ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-2" />
            )}
            AI Insights
          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-teal-500">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Monthly Revenue
              </CardDescription>
              <CardTitle className="text-3xl">{formatCurrency(overview.revenue.current_30d)}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-sm">
                {overview.revenue.change_percent >= 0 ? (
                  <>
                    <ArrowUpRight className="h-4 w-4 text-green-600" />
                    <span className="text-green-600 font-medium">+{overview.revenue.change_percent}%</span>
                  </>
                ) : (
                  <>
                    <ArrowDownRight className="h-4 w-4 text-red-600" />
                    <span className="text-red-600 font-medium">{overview.revenue.change_percent}%</span>
                  </>
                )}
                <span className="text-gray-500">vs prev. period</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Active Clinics
              </CardDescription>
              <CardTitle className="text-3xl">{formatNumber(overview.summary.active_clinics)}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-gray-500">
                {overview.summary.total_clinics} total registered
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Active Members
              </CardDescription>
              <CardTitle className="text-3xl">{formatNumber(overview.summary.active_members)}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-gray-500">
                +{overview.summary.new_members_30d} new this month
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Appointments
              </CardDescription>
              <CardTitle className="text-3xl">{formatNumber(overview.summary.total_appointments_30d)}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-gray-500">
                {overview.summary.completed_appointments_30d} completed
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-2xl grid-cols-5 mb-6">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="rankings" className="flex items-center gap-2">
            <Trophy className="h-4 w-4" />
            Rankings
          </TabsTrigger>
          <TabsTrigger value="revenue" className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Revenue
          </TabsTrigger>
          <TabsTrigger value="growth" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Growth
          </TabsTrigger>
          <TabsTrigger value="churn" className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Churn
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {overview && (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Tier Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Clinic Distribution by Tier</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={Object.entries(overview.distribution.clinics_by_tier).map(([name, value]) => ({
                          name: name.charAt(0).toUpperCase() + name.slice(1),
                          value
                        }))}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        fill="#8884d8"
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {Object.keys(overview.distribution.clinics_by_tier).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Key Metrics */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Key Performance Metrics</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">Estimated ARR</span>
                    <span className="font-bold text-xl text-teal-600">{formatCurrency(overview.revenue.arr)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">MRR</span>
                    <span className="font-bold text-xl">{formatCurrency(overview.revenue.mrr)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">Avg. Members/Clinic</span>
                    <span className="font-bold text-xl">
                      {overview.summary.active_clinics > 0 
                        ? Math.round(overview.summary.active_members / overview.summary.active_clinics) 
                        : 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">Appointment Rate</span>
                    <span className="font-bold text-xl">
                      {overview.summary.total_appointments_30d > 0 
                        ? Math.round(overview.summary.completed_appointments_30d / overview.summary.total_appointments_30d * 100) 
                        : 0}%
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Rankings Tab */}
        <TabsContent value="rankings" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                  <CardTitle>Clinic Performance Rankings</CardTitle>
                  <CardDescription>Top performing clinics by selected metric</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <select
                    value={rankingMetric}
                    onChange={(e) => setRankingMetric(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 bg-white text-sm"
                  >
                    <option value="revenue">Revenue</option>
                    <option value="members">Active Members</option>
                    <option value="appointments">Appointments</option>
                    <option value="retention">Retention Rate</option>
                  </select>
                  <Button variant="outline" size="sm" onClick={() => exportReport('rankings')}>
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {rankings?.rankings && (
                <div className="space-y-3">
                  {rankings.rankings.map((clinic, index) => (
                    <div 
                      key={clinic.clinic_id}
                      className={`flex items-center justify-between p-4 rounded-lg ${
                        index === 0 ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200' :
                        index === 1 ? 'bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200' :
                        index === 2 ? 'bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200' :
                        'bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                          index === 0 ? 'bg-amber-500 text-white' :
                          index === 1 ? 'bg-gray-400 text-white' :
                          index === 2 ? 'bg-orange-400 text-white' :
                          'bg-gray-200 text-gray-600'
                        }`}>
                          {clinic.rank}
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">{clinic.clinic_name}</h4>
                          <Badge variant="outline" className="mt-1">{clinic.subscription_tier}</Badge>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-gray-900">
                          {rankingMetric === 'revenue' ? formatCurrency(clinic.value) :
                           rankingMetric === 'retention' ? `${clinic.value}%` :
                           formatNumber(clinic.value)}
                        </div>
                        <div className="text-sm text-gray-500">{clinic.metric_label}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Revenue Tab */}
        <TabsContent value="revenue" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                  <CardTitle>Revenue Breakdown</CardTitle>
                  <CardDescription>Platform revenue analysis</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <select
                    value={breakdownBy}
                    onChange={(e) => setBreakdownBy(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 bg-white text-sm"
                  >
                    <option value="clinic">By Clinic</option>
                    <option value="tier">By Tier</option>
                    <option value="day">By Day</option>
                    <option value="month">By Month</option>
                  </select>
                  <Button variant="outline" size="sm" onClick={() => exportReport('revenue')}>
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {revenueBreakdown && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-lg border border-teal-200">
                    <div>
                      <div className="text-sm text-teal-600 font-medium">Total Revenue ({getPeriodLabel(period)})</div>
                      <div className="text-3xl font-bold text-teal-700">{formatCurrency(revenueBreakdown.total_revenue)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">Transactions</div>
                      <div className="text-2xl font-semibold">{formatNumber(revenueBreakdown.transaction_count)}</div>
                    </div>
                  </div>

                  <ResponsiveContainer width="100%" height={350}>
                    {(breakdownBy === 'day' || breakdownBy === 'month') ? (
                      <AreaChart data={Object.entries(revenueBreakdown.breakdown).map(([key, value]) => ({
                        name: breakdownBy === 'day' ? key.slice(5) : key,
                        revenue: value
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Area type="monotone" dataKey="revenue" stroke="#0D9488" fill="#0D948820" strokeWidth={2} />
                      </AreaChart>
                    ) : (
                      <BarChart data={Object.entries(revenueBreakdown.breakdown).slice(0, 10).map(([key, value]) => ({
                        name: key.length > 15 ? key.slice(0, 15) + '...' : key,
                        revenue: value
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                        <YAxis />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Bar dataKey="revenue" fill="#0D9488" />
                      </BarChart>
                    )}
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Growth Tab */}
        <TabsContent value="growth" className="space-y-6">
          {growthMetrics && (
            <>
              {/* Growth Rate Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-l-4 border-l-blue-500">
                  <CardHeader className="pb-2">
                    <CardDescription>Avg. Clinic Growth</CardDescription>
                    <CardTitle className="text-2xl flex items-center gap-2">
                      {growthMetrics.growth_rates.avg_clinic_growth > 0 ? (
                        <TrendingUp className="h-6 w-6 text-green-600" />
                      ) : (
                        <TrendingDown className="h-6 w-6 text-red-600" />
                      )}
                      {growthMetrics.growth_rates.avg_clinic_growth}%
                    </CardTitle>
                  </CardHeader>
                </Card>
                <Card className="border-l-4 border-l-purple-500">
                  <CardHeader className="pb-2">
                    <CardDescription>Avg. Member Growth</CardDescription>
                    <CardTitle className="text-2xl flex items-center gap-2">
                      {growthMetrics.growth_rates.avg_member_growth > 0 ? (
                        <TrendingUp className="h-6 w-6 text-green-600" />
                      ) : (
                        <TrendingDown className="h-6 w-6 text-red-600" />
                      )}
                      {growthMetrics.growth_rates.avg_member_growth}%
                    </CardTitle>
                  </CardHeader>
                </Card>
                <Card className="border-l-4 border-l-teal-500">
                  <CardHeader className="pb-2">
                    <CardDescription>Avg. Revenue Growth</CardDescription>
                    <CardTitle className="text-2xl flex items-center gap-2">
                      {growthMetrics.growth_rates.avg_revenue_growth > 0 ? (
                        <TrendingUp className="h-6 w-6 text-green-600" />
                      ) : (
                        <TrendingDown className="h-6 w-6 text-red-600" />
                      )}
                      {growthMetrics.growth_rates.avg_revenue_growth}%
                    </CardTitle>
                  </CardHeader>
                </Card>
              </div>

              {/* Growth Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>12-Month Growth Trends</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={400}>
                    <ComposedChart data={growthMetrics.monthly_data}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip formatter={(value, name) => [
                        name === 'revenue' ? formatCurrency(value) : value,
                        name.charAt(0).toUpperCase() + name.slice(1).replace('_', ' ')
                      ]} />
                      <Legend />
                      <Bar yAxisId="left" dataKey="new_clinics" fill="#3B82F6" name="New Clinics" />
                      <Bar yAxisId="left" dataKey="new_members" fill="#8B5CF6" name="New Members" />
                      <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#0D9488" strokeWidth={3} name="Revenue" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Totals */}
              <Card>
                <CardHeader>
                  <CardTitle>12-Month Totals</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-4xl font-bold text-blue-600">{formatNumber(growthMetrics.totals.total_new_clinics)}</div>
                      <div className="text-sm text-gray-600 mt-1">New Clinics</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="text-4xl font-bold text-purple-600">{formatNumber(growthMetrics.totals.total_new_members)}</div>
                      <div className="text-sm text-gray-600 mt-1">New Members</div>
                    </div>
                    <div className="text-center p-4 bg-teal-50 rounded-lg">
                      <div className="text-4xl font-bold text-teal-600">{formatCurrency(growthMetrics.totals.total_revenue)}</div>
                      <div className="text-sm text-gray-600 mt-1">Total Revenue</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Churn Tab */}
        <TabsContent value="churn" className="space-y-6">
          {churnAnalysis && (
            <>
              {/* Churn Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Total Members</CardDescription>
                    <CardTitle className="text-2xl">{formatNumber(churnAnalysis.summary.total_members)}</CardTitle>
                  </CardHeader>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Active Members</CardDescription>
                    <CardTitle className="text-2xl text-green-600">{formatNumber(churnAnalysis.summary.active_members)}</CardTitle>
                  </CardHeader>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Churned Members</CardDescription>
                    <CardTitle className="text-2xl text-red-600">{formatNumber(churnAnalysis.summary.churned_members)}</CardTitle>
                  </CardHeader>
                </Card>
                <Card className={`border-2 ${churnAnalysis.summary.churn_rate > 10 ? 'border-red-500' : 'border-green-500'}`}>
                  <CardHeader className="pb-2">
                    <CardDescription>Churn Rate</CardDescription>
                    <CardTitle className={`text-2xl ${churnAnalysis.summary.churn_rate > 10 ? 'text-red-600' : 'text-green-600'}`}>
                      {churnAnalysis.summary.churn_rate}%
                    </CardTitle>
                  </CardHeader>
                </Card>
              </div>

              {/* Churn by Clinic */}
              <Card>
                <CardHeader>
                  <CardTitle>Churn by Clinic</CardTitle>
                  <CardDescription>Clinics with highest member churn</CardDescription>
                </CardHeader>
                <CardContent>
                  {Object.keys(churnAnalysis.churn_by_clinic).length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={Object.entries(churnAnalysis.churn_by_clinic).slice(0, 10).map(([name, value]) => ({
                        name: name.length > 20 ? name.slice(0, 20) + '...' : name,
                        churned: value
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="churned" fill="#EF4444" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                      <p>No churn data available for this period</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* AI Insights Dialog */}
      <Dialog open={showAiDialog} onOpenChange={setShowAiDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              AI-Powered Platform Insights
            </DialogTitle>
            <DialogDescription>
              Strategic recommendations based on your platform data
            </DialogDescription>
          </DialogHeader>
          
          {aiInsights && (
            <div className="space-y-6 py-4">
              {/* Health Score */}
              {aiInsights.insights.platform_health_score && (
                <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
                  <div className="text-center">
                    <div className="text-5xl font-bold text-purple-600">{aiInsights.insights.platform_health_score}</div>
                    <div className="text-sm text-gray-600">Health Score</div>
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-700">{aiInsights.insights.health_assessment}</p>
                  </div>
                </div>
              )}

              {/* Key Insights */}
              {aiInsights.insights.key_insights && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Target className="h-4 w-4 text-teal-600" />
                    Key Insights
                  </h4>
                  <ul className="space-y-2">
                    {aiInsights.insights.key_insights.map((insight, index) => (
                      <li key={index} className="flex items-start gap-2 text-gray-700">
                        <ChevronRight className="h-4 w-4 text-teal-600 mt-1 flex-shrink-0" />
                        {insight}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Recommendations */}
              {aiInsights.insights.recommendations && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Strategic Recommendations</h4>
                  <div className="space-y-3">
                    {aiInsights.insights.recommendations.map((rec, index) => (
                      <div key={index} className="p-4 bg-gray-50 rounded-lg border">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-semibold text-gray-900">{rec.title}</h5>
                          <Badge className={
                            rec.priority === 'high' ? 'bg-red-100 text-red-700' :
                            rec.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                            'bg-green-100 text-green-700'
                          }>
                            {rec.priority} priority
                          </Badge>
                        </div>
                        <p className="text-gray-600 text-sm">{rec.description}</p>
                        {rec.impact && (
                          <p className="text-sm text-teal-600 mt-2 font-medium">Impact: {rec.impact}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Risk Areas */}
              {aiInsights.insights.risk_areas && aiInsights.insights.risk_areas.length > 0 && (
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <h4 className="font-semibold text-red-900 mb-3 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Risk Areas
                  </h4>
                  <ul className="space-y-2">
                    {aiInsights.insights.risk_areas.map((risk, index) => (
                      <li key={index} className="text-red-700 text-sm">• {risk}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdvancedReports;
